import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"
import { cdnUrl as paraCdnUrl } from "@/lib/utils/cdn"

/* O `alvo` (o alvo de toque de 44px, em `tailwind.config.ts`) entra na escala
   que o tailwind-merge conhece. Sem isso ele não sabia que `h-alvo` é uma
   altura: quem passava `className="h-12"` para o botão ficava com as duas
   classes, e quem ganhava era a ordem do CSS, e não quem chamou. */
const twMerge = extendTailwindMerge({ extend: { theme: { spacing: ["alvo"] } } })

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formata telefone brasileiro para exibição.
 * Aceita qualquer formato de entrada (com ou sem DDI 55, com ou sem máscara).
 *
 * Exemplos:
 *   "5521971307684"  → "(21) 97130-7684"
 *   "21971307684"    → "(21) 97130-7684"
 *   "2133445566"     → "(21) 3344-5566"
 *   ""               → ""
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return ""
  let digits = phone.replace(/\D/g, "")
  // Remove DDI brasileiro (+55) quando presente
  if (digits.startsWith("55") && digits.length >= 12) {
    digits = digits.substring(2)
  }
  // Celular: 11 dígitos (DDD + 9 + 8 dígitos)
  if (digits.length === 11) {
    return `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`
  }
  // Fixo: 10 dígitos (DDD + 8 dígitos)
  if (digits.length === 10) {
    return `(${digits.substring(0, 2)}) ${digits.substring(2, 6)}-${digits.substring(6)}`
  }
  return phone
}

/**
 * Formata valor monetário em BRL com separador de milhares.
 * `1737.41` → `"1.737,41"` (sem prefixo R$).
 */
export function formatBRL(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** O servidor respondeu com erro ao pedido do arquivo; a mensagem é a dele. */
export class ErroDoDownload extends Error {}

/**
 * Baixa um arquivo pela URL: no celular abre a folha de compartilhar, no
 * computador salva. Se o fetch falhar, abre a URL numa aba nova.
 *
 * `respostaDeErro`: quando o SERVIDOR responde com erro (e não a rede), lança o
 * erro com a `mensagem` que ele mandou, em vez de abrir a URL. É o caso de um
 * arquivo gerado na hora (o PDF assinado do contrato): abrir a aba mostraria o
 * erro cru, e quem chamou sabe escrever o que aconteceu.
 */
export async function forceDownload(url: string, filename: string, opcoes: { respostaDeErro?: boolean } = {}) {
  // Converter S3 para CDN se possível (CORS)
  const cdnUrl = paraCdnUrl(url)

  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent)

  // Estratégia 1: fetch + blob + share (mobile) ou download (desktop)
  try {
    const res = await fetch(cdnUrl)
    if (!res.ok) {
      if (opcoes.respostaDeErro) {
        const data = await res.json().catch(() => ({}) as { mensagem?: unknown })
        const mensagem = typeof data?.mensagem === "string" ? data.mensagem : "Não conseguimos baixar o arquivo agora. Tente de novo em instantes."
        throw new ErroDoDownload(mensagem)
      }
      throw new Error("fetch failed")
    }
    const blob = await res.blob()

    // Mobile: tentar share
    if (isMobile && navigator.share && navigator.canShare) {
      const file = new File([blob], filename, { type: blob.type || "application/octet-stream" })
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ title: filename, files: [file] })
        return
      }
    }

    // Desktop ou share não suportado: download via blob URL
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = blobUrl
    a.download = filename
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    }, 10000)
    return
  } catch (erro) {
    // fetch falhou (CORS ou rede)
    if (erro instanceof ErroDoDownload) throw erro
    /* Quem pediu o erro na tela também não quer a aba aberta quando a pessoa
       só fechou a folha de compartilhar. */
    if (opcoes.respostaDeErro && (erro as DOMException)?.name === "AbortError") return
  }

  // Estratégia 2: abrir URL direta em nova aba (browser lida com o download)
  const link = document.createElement("a")
  link.href = cdnUrl
  link.target = "_blank"
  link.rel = "noopener noreferrer"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
